import type { ReactElement } from 'react';

import { Children, forwardRef } from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

export interface SegmentedControlRootProps
  extends Omit<ToggleGroupPrimitive.ToggleGroupSingleProps, 'type'> {
  error?: boolean;
  changed?: boolean;
}

const rootVariants = cva(
  'group relative isolate grid h-10 auto-cols-fr grid-flow-col items-stretch gap-x-2.5 rounded-full border border-grey-mid bg-grey-light p-1 text-center disabled:cursor-not-allowed',
  {
    variants: {
      error: {
        true: 'border-pink',
      },
      changed: {
        true: 'border-purple',
      },
    },
  },
);

export const SegmentedControlRoot = forwardRef<HTMLDivElement, SegmentedControlRootProps>(
  (
    {
      hidden,
      error,
      changed,
      disabled,
      children,
      value: valueProp,
      defaultValue: defaultValueProp = '',
      onValueChange: onValueChangeProp,
      className,
      ...restProps
    },
    ref,
  ) => {
    const [value, setValue] = useControllableState({
      prop: valueProp,
      onChange: onValueChangeProp,
      defaultProp: defaultValueProp,
    });

    const numberOfItems = Children.count(children);
    const index = Children.toArray(children).findIndex(
      (child) => (child as ReactElement<SegmentedControlItemProps>).props.value === value,
    );

    const gap = ((numberOfItems - 1) * 10 + 8) / numberOfItems;

    if (hidden) {
      return null;
    }

    return (
      <ToggleGroupPrimitive.Root
        ref={ref}
        type="single"
        value={value}
        asChild={false}
        disabled={disabled}
        className={twMerge(rootVariants({ error, changed, className }))}
        onValueChange={(newValue) => {
          if (newValue) {
            setValue(newValue);
          }
        }}
        {...restProps}
      >
        {children}
        {index === -1 ? null : (
          <div
            className="pointer-events-none absolute inset-y-1 left-1 z-10 rounded-full bg-white shadow transition-transform duration-100 ease-segmented-toggle will-change-transform"
            style={{
              width: `calc(${100 / numberOfItems}% - ${gap}px)`,
              transform: `translateX(calc(${100 * index}% + ${gap * index}px))`,
            }}
          />
        )}
      </ToggleGroupPrimitive.Root>
    );
  },
);

SegmentedControlRoot.displayName = 'SegmentedControlRoot';

export interface SegmentedControlItemProps extends ToggleGroupPrimitive.ToggleGroupItemProps {}

export const SegmentedControlItem = forwardRef<HTMLButtonElement, SegmentedControlItemProps>(
  ({ children, className, disabled, value, ...restProps }, ref) => (
    <ToggleGroupPrimitive.Item
      ref={ref}
      value={value}
      disabled={disabled}
      asChild={false}
      className={twMerge(
        'z-20 cursor-pointer bg-transparent px-6 text-sm font-medium text-grey-dark transition-colors hover:text-blue disabled:cursor-not-allowed disabled:hover:text-grey-dark group-disabled:cursor-not-allowed data-[state=on]:text-blue',
        className,
      )}
      {...restProps}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  ),
);

SegmentedControlItem.displayName = 'SegmentedControlItem';
